import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { API_ENDPOINTS } from '../config/api';

// Types
export interface TemplateRating {
  templateId: string;
  rating: number;
  comment?: string;
  userId: string;
  createdAt: string;
}

export interface TemplateViewHistory {
  templateId: string;
  templateName: string;
  viewedAt: string;
}

export interface TemplateFavorite {
  templateId: string;
  templateName: string;
  addedAt: string;
}

export interface DocumentationCompleteness {
  hasDocumentation: boolean;
  hasSetupGuide: boolean;
  hasCodeExamples: boolean;
  hasUseCases: boolean;
  hasFaq: boolean;
  hasChangelog: boolean;
  hasLimitations: boolean;
  hasEnvironmentRequirements: boolean;
  percentage: number;
  sections: { name: string; filled: boolean }[];
}

// Local Storage Keys
const FAVORITES_KEY = 'provider-template-favorites';
const HISTORY_KEY = 'provider-template-history';
const MAX_HISTORY_ITEMS = 10;

// Hook for managing favorites (local storage based)
export function useTemplateFavorites() {
  const [favorites, setFavorites] = useState<TemplateFavorite[]>([]);

  useEffect(() => {
    const stored = localStorage.getItem(FAVORITES_KEY);
    if (stored) {
      try {
        setFavorites(JSON.parse(stored));
      } catch {
        setFavorites([]);
      }
    }
  }, []);

  const saveFavorites = useCallback((newFavorites: TemplateFavorite[]) => {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(newFavorites));
    setFavorites(newFavorites);
  }, []);

  const addFavorite = useCallback((templateId: string, templateName: string) => {
    const newFavorite: TemplateFavorite = {
      templateId,
      templateName,
      addedAt: new Date().toISOString(),
    };
    const updated = [...favorites.filter(f => f.templateId !== templateId), newFavorite];
    saveFavorites(updated);
  }, [favorites, saveFavorites]);

  const removeFavorite = useCallback((templateId: string) => {
    const updated = favorites.filter(f => f.templateId !== templateId);
    saveFavorites(updated);
  }, [favorites, saveFavorites]);

  const isFavorite = useCallback((templateId: string) => {
    return favorites.some(f => f.templateId === templateId);
  }, [favorites]);

  const toggleFavorite = useCallback((templateId: string, templateName: string) => {
    if (isFavorite(templateId)) {
      removeFavorite(templateId);
    } else {
      addFavorite(templateId, templateName);
    }
  }, [isFavorite, removeFavorite, addFavorite]);

  return {
    favorites,
    addFavorite,
    removeFavorite,
    isFavorite,
    toggleFavorite,
    favoritesCount: favorites.length,
  };
}

// Hook for managing view history (local storage based)
export function useTemplateHistory() {
  const [history, setHistory] = useState<TemplateViewHistory[]>([]);

  useEffect(() => {
    const stored = localStorage.getItem(HISTORY_KEY);
    if (stored) {
      try {
        setHistory(JSON.parse(stored));
      } catch {
        setHistory([]);
      }
    }
  }, []);

  const saveHistory = useCallback((newHistory: TemplateViewHistory[]) => {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory));
    setHistory(newHistory);
  }, []);

  const addToHistory = useCallback((templateId: string, templateName: string) => {
    const newEntry: TemplateViewHistory = {
      templateId,
      templateName,
      viewedAt: new Date().toISOString(),
    };
    // Remove existing entry for same template and add to front
    const filtered = history.filter(h => h.templateId !== templateId);
    const updated = [newEntry, ...filtered].slice(0, MAX_HISTORY_ITEMS);
    saveHistory(updated);
  }, [history, saveHistory]);

  const clearHistory = useCallback(() => {
    saveHistory([]);
  }, [saveHistory]);

  const recentTemplates = useMemo(() => {
    return history.slice(0, 5);
  }, [history]);

  return {
    history,
    addToHistory,
    clearHistory,
    recentTemplates,
  };
}

// Hook for template ratings (API based)
export function useTemplateRatings(templateId?: string) {
  const queryClient = useQueryClient();

  const { data: ratings, isLoading } = useQuery({
    queryKey: ['template-ratings', templateId],
    queryFn: async () => {
      if (!templateId) return null;
      try {
        const response = await api.get(`${API_ENDPOINTS.providerEngine.templates}/${templateId}/ratings`);
        return response.data;
      } catch {
        return { averageRating: 0, totalRatings: 0, ratings: [] };
      }
    },
    enabled: !!templateId,
  });

  const submitRating = useMutation({
    mutationFn: async ({ rating, comment }: { rating: number; comment?: string }) => {
      if (!templateId) throw new Error('Template ID required');
      const response = await api.post(`${API_ENDPOINTS.providerEngine.templates}/${templateId}/ratings`, {
        rating,
        comment,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template-ratings', templateId] });
    },
  });

  return {
    ratings,
    isLoading,
    submitRating: submitRating.mutate,
    isSubmitting: submitRating.isPending,
  };
}

// Hook for calculating documentation completeness
export function useDocumentationCompleteness(template: any): DocumentationCompleteness {
  return useMemo(() => {
    const sections = [
      { name: 'Documentação', filled: !!template?.documentation },
      { name: 'Guia de Setup', filled: !!template?.setupGuide },
      { name: 'Exemplos de Código', filled: (template?.codeExamples?.length || 0) > 0 },
      { name: 'Casos de Uso', filled: (template?.useCases?.length || 0) > 0 },
      { name: 'FAQ', filled: (template?.faq?.length || 0) > 0 },
      { name: 'Changelog', filled: (template?.changelog?.length || 0) > 0 },
      { name: 'Limitações', filled: (template?.knownLimitations?.length || 0) > 0 },
      { name: 'Requisitos de Ambiente', filled: (template?.environmentRequirements?.length || 0) > 0 },
    ];

    const filledCount = sections.filter(s => s.filled).length;
    const percentage = Math.round((filledCount / sections.length) * 100);

    return {
      hasDocumentation: !!template?.documentation,
      hasSetupGuide: !!template?.setupGuide,
      hasCodeExamples: (template?.codeExamples?.length || 0) > 0,
      hasUseCases: (template?.useCases?.length || 0) > 0,
      hasFaq: (template?.faq?.length || 0) > 0,
      hasChangelog: (template?.changelog?.length || 0) > 0,
      hasLimitations: (template?.knownLimitations?.length || 0) > 0,
      hasEnvironmentRequirements: (template?.environmentRequirements?.length || 0) > 0,
      percentage,
      sections,
    };
  }, [template]);
}

// Hook for searching within documentation
export function useDocumentationSearch(content: string, searchTerm: string) {
  return useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) {
      return { matches: [], highlightedContent: content };
    }

    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const matches: { index: number; text: string; context: string }[] = [];
    
    let match;
    while ((match = regex.exec(content)) !== null) {
      const start = Math.max(0, match.index - 50);
      const end = Math.min(content.length, match.index + searchTerm.length + 50);
      matches.push({
        index: match.index,
        text: match[0],
        context: '...' + content.slice(start, end) + '...',
      });
    }

    const highlightedContent = content.replace(regex, '<mark class="bg-yellow-300 dark:bg-yellow-600">$1</mark>');

    return { matches, highlightedContent, matchCount: matches.length };
  }, [content, searchTerm]);
}

// Hook for template comparison
export function useTemplateComparison() {
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);

  const addToComparison = useCallback((templateId: string) => {
    setSelectedTemplates(prev => {
      if (prev.includes(templateId)) return prev;
      if (prev.length >= 2) return prev; // Max 2 templates
      return [...prev, templateId];
    });
  }, []);

  const removeFromComparison = useCallback((templateId: string) => {
    setSelectedTemplates(prev => prev.filter(id => id !== templateId));
  }, []);

  const clearComparison = useCallback(() => {
    setSelectedTemplates([]);
  }, []);

  const isInComparison = useCallback((templateId: string) => {
    return selectedTemplates.includes(templateId);
  }, [selectedTemplates]);

  const canAddMore = selectedTemplates.length < 2;

  return {
    selectedTemplates,
    addToComparison,
    removeFromComparison,
    clearComparison,
    isInComparison,
    canAddMore,
    comparisonCount: selectedTemplates.length,
  };
}

// Hook for feature-based filtering
export interface FeatureFilter {
  hasWebhooks: boolean;
  hasOAuth: boolean;
  hasApiKey: boolean;
  hasMultipleOperations: boolean;
}

export function useFeatureFilter(templates: any[]) {
  const [filters, setFilters] = useState<FeatureFilter>({
    hasWebhooks: false,
    hasOAuth: false,
    hasApiKey: false,
    hasMultipleOperations: false,
  });

  const filteredTemplates = useMemo(() => {
    return templates.filter(template => {
      if (filters.hasWebhooks && !template.config?.webhookConfig?.enabled) return false;
      if (filters.hasOAuth && template.config?.authConfig?.type !== 'oauth2') return false;
      if (filters.hasApiKey && template.config?.authConfig?.type !== 'api_key') return false;
      if (filters.hasMultipleOperations && (template.config?.operations?.length || 0) < 3) return false;
      return true;
    });
  }, [templates, filters]);

  const toggleFilter = useCallback((key: keyof FeatureFilter) => {
    setFilters(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      hasWebhooks: false,
      hasOAuth: false,
      hasApiKey: false,
      hasMultipleOperations: false,
    });
  }, []);

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return {
    filters,
    filteredTemplates,
    toggleFilter,
    clearFilters,
    activeFilterCount,
  };
}

export default {
  useTemplateFavorites,
  useTemplateHistory,
  useTemplateRatings,
  useDocumentationCompleteness,
  useDocumentationSearch,
  useTemplateComparison,
  useFeatureFilter,
};
